import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "@i18n";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import UserNameTagComponent from "../../../cubeComponent/html/UserNameTagComponent";
import IconComponent from "@CubeComponents/icons";
import { usePortalChat, messageTime, conversationDisplayTime } from "./context";
import { VirtualizedList } from "./VirtualizedList";
import {
  ATTACHMENT_ROW_HEIGHT,
  PICKER_ROW_HEIGHT,
  MESSAGE_TEXT_MAX_LENGTH,
  SEARCH_MAX_LENGTH,
} from "../portalChat.constants";
import { toObjectIdString } from "@Helpers/schemas/common.schema";
import {
  getMessageReceiptsRequest,
  sendMessageRequest,
  type ChatMessage,
  type GetMessageReceiptsResponseData,
} from "../api/portalChat.api";

/** Forward a message into any other conversation, with an optional note above the card (PRD). */
export function ForwardDialog({ message, onClose }: { readonly message: ChatMessage; readonly onClose: () => void }) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const [search, setSearch] = useState("");
  const [note, setNote] = useState("");
  const [targetId, setTargetId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const candidates = useMemo(() => {
    const lower = search.trim().toLowerCase();
    return chat.conversations.filter((c) => !lower || c.name.toLowerCase().includes(lower));
  }, [chat.conversations, search]);

  const send = async () => {
    if (!targetId || sending) return;
    setSending(true);
    try {
      const response = await sendMessageRequest({
        conversationId: toObjectIdString(targetId),
        forwardOfMessageId: toObjectIdString(message._id),
        ...(note.trim() ? { text: note.trim() } : {}),
      });
      if (response.data.status) {
        chat.showToast(t("Message forwarded"));
        await chat.refreshList();
        // PRD §4.4: forwarding takes the user to the destination chat, un-hiding it if necessary.
        chat.unhideAndOpen(targetId);
        onClose();
      } else {
        chat.showServerError(response.data.errorMessage, t("Could not forward the message"));
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalStateDialogComponent
      isVisible
      width="30rem"
      headerTitle={t("Forward this message")}
      onCancel={onClose}
      modelContent={
        <div className="flex max-h-[60vh] w-full flex-col gap-2">
          <div className="rounded-xs border border-[#e3ecf5] bg-[#f9fbfe] px-2 py-1 text-xs">
            <span className="font-semibold">{message.senderName}: </span>
            <span className="line-clamp-2">{message.text || t("Attachment")}</span>
          </div>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={MESSAGE_TEXT_MAX_LENGTH}
            placeholder={t("Add a note (optional)")}
            className="w-full rounded-xs border border-[#dce6f1] px-2 py-1 text-sm outline-hidden"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            maxLength={SEARCH_MAX_LENGTH}
            placeholder={t("Search chats")}
            className="w-full rounded-xs border border-[#dce6f1] px-2 py-1 text-sm outline-hidden"
          />
          <div className="h-64">
            {candidates.length > 0 && (
              <VirtualizedList
                items={candidates}
                rowHeight={ATTACHMENT_ROW_HEIGHT}
                ariaLabel={t("Search chats")}
                renderRow={(conversation) => (
                  <button
                    type="button"
                    onClick={() => setTargetId(conversation._id)}
                    className={`flex h-full w-full items-center justify-between px-2 text-start text-sm hover:bg-[#f0f5f9] ${
                      targetId === conversation._id ? "bg-[#e8f1fc]" : ""
                    }`}
                  >
                    <span className="truncate">{conversation.name}</span>
                    {conversation.lastMessageDate && (
                      <span className="text-xs text-gray-400">
                        {conversationDisplayTime(conversation.lastMessageDate)}
                      </span>
                    )}
                  </button>
                )}
              />
            )}
            {!candidates.length && <div className="p-3 text-sm text-gray-400">{t("No chats match your search.")}</div>}
          </div>
        </div>
      }
      footerContent={
        <div className="flex justify-end gap-2">
          <ButtonComponent buttonType="borderButton" onClick={onClose}>
            {t("Cancel")}
          </ButtonComponent>
          <ButtonComponent buttonType="gradientButton" onClick={() => void send()} disabled={!targetId || sending}>
            {t("Forward")}
          </ButtonComponent>
        </div>
      }
    />
  );
}

/**
 * Edit-message pop-up (PRD §4.15): pre-filled with the message's CURRENT text, shows the original
 * text and timestamp for reference (and the quoted reply context when the message was a reply),
 * Save disabled while empty, Enter saves, Escape discards. Fully independent of the composer draft.
 */
export function EditMessageDialog({
  message,
  onClose,
}: {
  readonly message: ChatMessage;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const [text, setText] = useState(message.text ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmed = text.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    const ok = await chat.editMessage(message._id, trimmed);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <ModalStateDialogComponent
      isVisible
      width="28rem"
      headerTitle={t("Edit message")}
      onCancel={onClose}
      modelContent={
        <div className="flex w-full flex-col gap-2">
          {message.replyTo && (
            <div className="rounded-xs border-s-2 border-[#6BBBE3] bg-[#f6fafd] px-2 py-1 text-xs">
              <div className="font-semibold text-[#1E88E5]">{message.replyTo.senderName}</div>
              <div className="truncate text-gray-500">{message.replyTo.text || t("Attachment")}</div>
            </div>
          )}
          <textarea
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MESSAGE_TEXT_MAX_LENGTH))}
            maxLength={MESSAGE_TEXT_MAX_LENGTH}
            rows={4}
            className="w-full resize-none rounded-xs border border-[#dce6f1] px-2 py-1.5 text-sm outline-hidden focus:border-[#6BBBE3]"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void save();
              }
              if (e.key === "Escape") onClose();
            }}
          />
          <div className="text-xs text-gray-400">
            {t("Sent")} {new Date(message.createdDate).toLocaleString()}
            {message.editedDate ? ` · ${t("edited")}` : ""}
          </div>
        </div>
      }
      footerContent={
        <div className="flex justify-end gap-2">
          <ButtonComponent buttonType="borderButton" onClick={onClose}>
            {t("Cancel")}
          </ButtonComponent>
          <ButtonComponent buttonType="gradientButton" disabled={!text.trim() || saving} onClick={() => void save()}>
            {t("Save")}
          </ButtonComponent>
        </div>
      }
    />
  );
}

/** Read-receipt details for one message: who has read it, who hasn't (uncapped, PRD). */
export function ReceiptsDialog({ message, onClose }: { readonly message: ChatMessage; readonly onClose: () => void }) {
  const { t } = useTranslation();
  const [data, setData] = useState<GetMessageReceiptsResponseData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getMessageReceiptsRequest({ messageId: toObjectIdString(message._id) })
      .then((response) => {
        if (!cancelled && response.data.status) {
          setData({
            message: response.data.message,
            readBy: response.data.readBy ?? [],
            unreadBy: response.data.unreadBy ?? [],
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [message._id]);

  /** Flat rows for the virtualized receipts list — a space chat can have thousands of members. */
  type ReceiptRow =
    | { kind: "header"; key: string; label: string }
    | { kind: "empty"; key: string }
    | { kind: "reader"; key: string; userId: string; fullName: string; lastReadDate?: number };

  const receiptRows = useMemo((): ReceiptRow[] => {
    if (!data) return [];
    const rows: ReceiptRow[] = [{ kind: "header", key: "read", label: `${t("Read by")} (${data.readBy.length})` }];
    for (const reader of data.readBy) {
      rows.push({
        kind: "reader",
        key: `read-${String(reader._id)}`,
        userId: String(reader._id),
        fullName: reader.fullName,
        lastReadDate: reader.lastReadDate,
      });
    }
    if (!data.readBy.length) rows.push({ kind: "empty", key: "read-empty" });
    if (data.unreadBy.length) {
      rows.push({ kind: "header", key: "unread", label: `${t("Not read yet")} (${data.unreadBy.length})` });
      for (const member of data.unreadBy) {
        rows.push({
          kind: "reader",
          key: `unread-${String(member._id)}`,
          userId: String(member._id),
          fullName: member.fullName,
        });
      }
    }
    return rows;
  }, [data, t]);

  return (
    <ModalStateDialogComponent
      isVisible
      width="26rem"
      headerTitle={t("Message details")}
      onCancel={onClose}
      footerContent={<></>}
      modelContent={
        <div className="flex max-h-[60vh] w-full flex-col gap-2 overflow-y-auto">
          {loading && <div className="text-sm text-gray-400">{t("Loading")}…</div>}
          {data && (
            <>
              <div className="rounded-xs border border-[#e3ecf5] bg-[#f9fbfe] px-2 py-1 text-xs">
                <div className="font-semibold">{data.message?.senderName}</div>
                <div className="line-clamp-3">{data.message?.text || t("Attachment")}</div>
                <div className="mt-0.5 text-gray-400">
                  {data.message ? new Date(data.message.createdDate).toLocaleString() : ""}
                </div>
              </div>
              <div className="h-[40vh]">
                <VirtualizedList
                  items={receiptRows}
                  ariaLabel={t("Message details")}
                  rowHeight={(row) => (row.kind === "empty" ? ATTACHMENT_ROW_HEIGHT : PICKER_ROW_HEIGHT)}
                  renderRow={(row) => {
                    if (row.kind === "header") {
                      return (
                        <div className="flex h-full items-end pb-1 text-xs font-semibold text-gray-500">
                          {row.label}
                        </div>
                      );
                    }
                    if (row.kind === "empty") {
                      return (
                        <div className="flex h-full items-center text-xs text-gray-400">
                          {t("No one has read this yet.")}
                        </div>
                      );
                    }
                    return (
                      <div className="flex h-full items-center justify-between">
                        <span className="flex items-center gap-2 text-sm">
                          <UserNameTagComponent showAvatarOnly id={row.userId} size="XS" isCircle />
                          {row.fullName}
                        </span>
                        {row.lastReadDate !== undefined && (
                          <span className="text-xs text-gray-400">{messageTime(row.lastReadDate)}</span>
                        )}
                      </div>
                    );
                  }}
                />
              </div>
            </>
          )}
        </div>
      }
    />
  );
}

/** "View reactions" dialog: per-emoji tabs, remove own only (PRD). */
export function ReactionsDialog({ message, onClose }: { readonly message: ChatMessage; readonly onClose: () => void }) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  // Reactions may change while the dialog is open; read the live copy out of the thread.
  const liveMessage = chat.thread?.messages.find((m) => m._id === message._id) ?? message;
  const reactions = liveMessage.reactions ?? [];
  const [tab, setTab] = useState<string>("all");

  const memberName = (id: string) =>
    chat.thread?.members.find((member) => String(member._id) === id)?.fullName ?? t("Unknown user");

  const rows =
    tab === "all"
      ? reactions.flatMap((reaction) => reaction.users.map((u) => ({ emoji: reaction.emoji, userId: String(u) })))
      : (reactions.find((reaction) => reaction.emoji === tab)?.users ?? []).map((u) => ({
          emoji: tab,
          userId: String(u),
        }));

  const total = reactions.reduce((sum, reaction) => sum + reaction.users.length, 0);

  return (
    <ModalStateDialogComponent
      isVisible
      width="24rem"
      headerTitle={
        <span>
          {t("Reactions")} ({total})
        </span>
      }
      onCancel={onClose}
      footerContent={<></>}
      modelContent={
        <div className="flex max-h-[55vh] w-full flex-col gap-2">
          <div className="rounded-xs border border-[#e3ecf5] bg-[#f9fbfe] px-2 py-1 text-xs">
            <span className="font-semibold">{liveMessage.senderName}: </span>
            <span className="line-clamp-2">
              {liveMessage.text || liveMessage.attachments?.[0]?.attachmentsData.name || t("Attachment")}
            </span>
          </div>
          {!total && <div className="p-3 text-sm text-gray-400">{t("No reactions yet")}</div>}
          {total > 0 && (
            <>
              <div className="flex flex-wrap gap-1">
                <button
                  type="button"
                  onClick={() => setTab("all")}
                  className={`rounded-full border px-2 py-0.5 text-xs ${
                    tab === "all" ? "border-[#6BBBE3] bg-[#e8f1fc]" : "border-[#e3ecf5]"
                  }`}
                >
                  {t("All")} {total}
                </button>
                {reactions.map((reaction) => (
                  <button
                    key={reaction.emoji}
                    type="button"
                    onClick={() => setTab(reaction.emoji)}
                    className={`rounded-full border px-2 py-0.5 text-xs ${
                      tab === reaction.emoji ? "border-[#6BBBE3] bg-[#e8f1fc]" : "border-[#e3ecf5]"
                    }`}
                  >
                    {reaction.emoji} {reaction.users.length}
                  </button>
                ))}
              </div>
              <div className="flex flex-col gap-0.5">
                {reactions
                  .filter((reaction) => tab === "all" || reaction.emoji === tab)
                  .map((reaction) => {
                    const names = reaction.users.map((u) =>
                      String(u) === chat.userId ? t("You") : memberName(String(u)),
                    );
                    const last = names.at(-1) ?? "";
                    const sentence = names.length <= 1 ? last : `${names.slice(0, -1).join(", ")} ${t("and")} ${last}`;
                    return (
                      <div key={`sentence-${reaction.emoji}`} className="text-xs text-gray-500">
                        {t("{{names}} reacted with", { names: sentence })} {reaction.emoji}
                      </div>
                    );
                  })}
              </div>
              <div className="h-[35vh]">
                <VirtualizedList
                  items={rows}
                  rowHeight={ATTACHMENT_ROW_HEIGHT}
                  ariaLabel={t("Reactions")}
                  renderRow={(row) => (
                    <div className="flex h-full items-center justify-between px-1">
                      <span className="flex items-center gap-2 text-sm">
                        <UserNameTagComponent showAvatarOnly id={row.userId} size="XS" isCircle />
                        {row.userId === chat.userId ? t("You") : memberName(row.userId)}
                      </span>
                      <span className="flex items-center gap-2">
                        <span>{row.emoji}</span>
                        {row.userId === chat.userId && (
                          <span
                            role="button"
                            tabIndex={0}
                            title={t("Remove")}
                            onClick={() => void chat.toggleReaction(liveMessage._id, row.emoji)}
                            onKeyDown={() => undefined}
                          >
                            <IconComponent name="CrossIcon" width="0.7rem" height="0.7rem" />
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                />
              </div>
            </>
          )}
        </div>
      }
    />
  );
}
